import React, { useState, useEffect } from 'react';
import { X, Loader2, Calendar, FileText } from 'lucide-react';
import { conciergeAPI, ConciergeReservation } from '../../api/concierge';
import { Button } from '../ui/Button';
import { pdf } from '@react-pdf/renderer';
import { ReservationAttestationPDF } from '../pdf/ReservationAttestationPDF';

interface ReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  propertyId: number;
  reservation?: ConciergeReservation | null;
  prefilledStartDate?: string | null;
}

export const ReservationModal: React.FC<ReservationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  propertyId,
  reservation,
  prefilledStartDate,
}) => {
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_phone: '',
    start_date: '',
    end_date: '',
    price_per_night: '',
    amount_paid: '',
    notes: '',
    is_pre_reservation: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (reservation) {
      setFormData({
        guest_name: reservation.guest_name,
        guest_phone: reservation.guest_phone || '',
        start_date: formatDateForInput(reservation.start_date),
        end_date: formatDateForInput(reservation.end_date),
        price_per_night: reservation.price_per_night.toString(),
        amount_paid: reservation.amount_paid.toString(),
        notes: reservation.notes || '',
        is_pre_reservation: reservation.is_pre_reservation || false,
      });
    } else {
      setFormData({
        guest_name: '',
        guest_phone: '',
        start_date: prefilledStartDate || '',
        end_date: '',
        price_per_night: '',
        amount_paid: '',
        notes: '',
        is_pre_reservation: false,
      });
    }
    setError(null);
  }, [reservation, prefilledStartDate, isOpen]);

  const calculateNights = (): number => {
    if (!formData.start_date || !formData.end_date) return 0;
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const calculateTotal = (): number => {
    const nights = calculateNights();
    const pricePerNight = parseFloat(formData.price_per_night) || 0;
    return nights * pricePerNight;
  };

  const calculateBalance = (): number => {
    const total = calculateTotal();
    const paid = parseFloat(formData.amount_paid) || 0;
    return total - paid;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const finalValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.guest_name.trim()) {
      setError('Le nom du locataire est requis');
      return;
    }

    if (!formData.start_date || !formData.end_date) {
      setError('Les dates sont requises');
      return;
    }

    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      setError('La date de fin doit être après la date de début');
      return;
    }

    const pricePerNight = parseFloat(formData.price_per_night);
    if (isNaN(pricePerNight) || pricePerNight <= 0) {
      setError('Le prix par nuit doit être un nombre positif');
      return;
    }

    const amountPaid = parseFloat(formData.amount_paid);
    if (isNaN(amountPaid) || amountPaid < 0) {
      setError('Le montant versé doit être un nombre positif ou zéro');
      return;
    }

    const total = calculateTotal();
    if (amountPaid > total) {
      setError('Le montant versé ne peut pas dépasser le coût total');
      return;
    }

    setIsSubmitting(true);

    try {
      const reservationData: ConciergeReservation = {
        property_id: propertyId,
        guest_name: formData.guest_name.trim(),
        guest_phone: formData.guest_phone.trim() || undefined,
        start_date: formData.start_date,
        end_date: formData.end_date,
        price_per_night: pricePerNight,
        amount_paid: amountPaid,
        notes: formData.notes.trim() || undefined,
        is_pre_reservation: formData.is_pre_reservation,
      };

      if (reservation?.id) {
        await conciergeAPI.updateReservation(reservation.id, reservationData);
      } else {
        await conciergeAPI.createReservation(reservationData);
      }

      onSave();
    } catch (err) {
      console.error('Error saving reservation:', err);
      setError('Erreur lors de la sauvegarde de la réservation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!reservation?.id) return;

    try {
      // Récupérer les infos de l'agence depuis localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        alert('Erreur: Informations utilisateur non disponibles');
        return;
      }

      const user = JSON.parse(userStr);
      const agencyId = user.agency_id;

      // Récupérer les infos de l'agence depuis l'API
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${baseUrl}/agencies/${agencyId}`);

      if (!response.ok) {
        throw new Error('Impossible de récupérer les informations de l\'agence');
      }

      const agencyData = await response.json();

      const agencyInfo = {
        name: agencyData.name || 'Agence Immobilière',
        address: agencyData.address || '',
        phone: agencyData.contact_phone || '',
        email: agencyData.contact_email || undefined,
        registration_number: agencyData.registration_number || undefined,
      };

      // Générer le PDF
      const doc = (
        <ReservationAttestationPDF
          reservationId={reservation.id}
          guestName={formData.guest_name}
          guestPhone={formData.guest_phone}
          startDate={formData.start_date}
          endDate={formData.end_date}
          pricePerNight={parseFloat(formData.price_per_night)}
          amountPaid={parseFloat(formData.amount_paid)}
          notes={formData.notes}
          agencyInfo={agencyInfo}
        />
      );

      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation-reservation-${String(reservation.id).padStart(4, '0')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération de l\'attestation PDF');
    }
  };

  if (!isOpen) return null;

  const nights = calculateNights();
  const total = calculateTotal();
  const balance = calculateBalance();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="relative w-full max-w-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white dark:bg-gray-800 p-4 sm:p-6 shadow-xl">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 sm:right-4 sm:top-4 h-9 w-9 sm:h-auto sm:w-auto flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 touch-manipulation"
          disabled={isSubmitting}
        >
          <X className="h-5 w-5 sm:h-5 sm:w-5" />
        </button>

        <div className="mb-4 sm:mb-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
            <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
              {reservation ? 'Modifier la réservation' : 'Nouvelle réservation'}
            </h2>
          </div>
        </div>

        {error && (
          <div className="mb-3 sm:mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2 sm:p-3">
            <p className="text-red-800 dark:text-red-200 text-xs sm:text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nom du locataire *
              </label>
              <input
                type="text"
                name="guest_name"
                value={formData.guest_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Téléphone
              </label>
              <input
                type="tel"
                name="guest_phone"
                value={formData.guest_phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date de début *
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date de fin *
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prix par nuit (DA) *
              </label>
              <input
                type="number"
                name="price_per_night"
                value={formData.price_per_night}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Montant versé (DA) *
              </label>
              <input
                type="number"
                name="amount_paid"
                value={formData.amount_paid}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          {formData.start_date && formData.end_date && formData.price_per_night && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4">
              <h3 className="text-xs sm:text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2 sm:mb-3">
                Calculs automatiques
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <p className="text-blue-600 dark:text-blue-400 mb-1 text-[10px] sm:text-xs">Nombre de nuits</p>
                  <p className="text-base sm:text-lg font-bold text-blue-900 dark:text-blue-100">
                    {nights}
                  </p>
                </div>
                <div>
                  <p className="text-blue-600 dark:text-blue-400 mb-1 text-[10px] sm:text-xs">Coût total</p>
                  <p className="text-base sm:text-lg font-bold text-blue-900 dark:text-blue-100">
                    {total.toLocaleString('fr-FR')} DA
                  </p>
                </div>
                <div>
                  <p className="text-blue-600 dark:text-blue-400 mb-1 text-[10px] sm:text-xs">Solde restant</p>
                  <p className={`text-base sm:text-lg font-bold ${
                    balance > 0
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-green-600 dark:text-green-400'
                  }`}>
                    {balance.toLocaleString('fr-FR')} DA
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-center space-x-2 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <input
              type="checkbox"
              id="is_pre_reservation"
              name="is_pre_reservation"
              checked={formData.is_pre_reservation}
              onChange={handleChange}
              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 dark:bg-gray-700 dark:border-gray-600"
              disabled={isSubmitting}
            />
            <label htmlFor="is_pre_reservation" className="text-xs sm:text-sm font-medium text-orange-700 dark:text-orange-300 cursor-pointer">
              Pré-réservation (sera annulée automatiquement après 48h si non confirmée)
            </label>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-between items-center space-y-2 space-y-reverse sm:space-y-0 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Bouton Attestation PDF à gauche (visible uniquement en mode édition) */}
            {reservation?.id && (
              <Button
                type="button"
                variant="secondary"
                onClick={handleGeneratePDF}
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                <FileText className="w-4 h-4 mr-2" />
                Attestation PDF
              </Button>
            )}

            {/* Boutons Annuler et Enregistrer à droite */}
            <div className="flex flex-col-reverse sm:flex-row space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="!bg-green-600 hover:!bg-green-700 w-full sm:w-auto"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
